import React from 'react';
import type { ComponentMetadata } from './types';
import { REQUIRED_SYSTEM_MODULES } from './types';

// Re-export pour compatibilité
export type { ComponentMetadata };

// Chargement automatique de tous les composants depuis le dossier Module
const componentModules = import.meta.glob('../../Module/**/*[!_config].tsx', { eager: true });
const configModules = import.meta.glob('../../Module/**/*_config.ts', { eager: true });

// Registres initialisés dynamiquement
export const componentRegistry: Record<string, React.ComponentType<any>> = {};
export const componentMetadataRegistry: Record<string, ComponentMetadata> = {};

// Charger automatiquement tous les configs
Object.entries(configModules).forEach(([, module]) => {
    const configExport = module as { default?: ComponentMetadata };
    if (configExport.default) {
        const metadata = configExport.default;
        componentMetadataRegistry[metadata.name] = metadata;
    }
});

// Charger automatiquement tous les composants
Object.entries(componentModules).forEach(([path, module]) => {
    const componentExport = module as { default?: React.ComponentType<any> };
    if (componentExport.default) {
        // Extraire le nom du composant depuis le chemin
        // Ex: "../../Module/button/buttonModule.tsx" -> "buttonModule"
        const match = path.match(/Module\/(.+?)\/([^/]+)\.tsx$/);
        if (match) {
            const componentName = match[2];
            componentRegistry[componentName] = componentExport.default;
        }
    }
});

// Valider que les modules système requis sont présents
export function validateSystemModules(): string[] {
    const missing = REQUIRED_SYSTEM_MODULES.filter(
        name => !componentRegistry[name]
    );
    
    if (missing.length > 0) {
        console.warn(
            `⚠️ Modules système manquants: ${missing.join(', ')}\n` +
            `Ces modules sont requis pour le bon fonctionnement du système.\n` +
            `Veuillez créer ces modules dans src/Module/`
        );
    }
    
    return missing;
}

// Log une seule fois au chargement
if (Object.keys(componentRegistry).length > 0) {
    console.log('🚀 Système de modules chargé:', {
        composants: Object.keys(componentRegistry),
        configs: Object.keys(componentMetadataRegistry)
    });
    
    // Valider les modules système
    const missing = validateSystemModules();
    if (missing.length === 0) {
        console.log('✅ Tous les modules système requis sont présents');
    }
}

export function getRegisteredComponent(name: string): React.ComponentType<any> | null {
    return componentRegistry[name] ?? null;
}

export function getComponentMetadata(name: string): ComponentMetadata | null {
    return componentMetadataRegistry[name] ?? null;
}

export function getAllComponents(): ComponentMetadata[] {
    return Object.values(componentMetadataRegistry);
}

export function getComponentRegistry(): Record<string, ComponentMetadata> {
    return componentMetadataRegistry;
}
